import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n/I18nContext";
import { fetchProducts } from "../services/products.service";
import { fetchCategories } from "../services/categories.service";
import ProductPreviewModal from "../components/ProductPreviewModal";

export default function ProductsPage({ initialCategoryId }) {
  const [activeCategory, setActiveCategory] = useState("all");
  const { t, lang } = useI18n();

  const [allProducts, setAllProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Set initial category from navigation
  useEffect(() => {
    if (initialCategoryId) setActiveCategory(String(initialCategoryId));
    else setActiveCategory("all");
  }, [initialCategoryId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const langMap = { AR: "ar", EN: "en", TR: "tr" };
    const apiLang = langMap[lang] || "ar";

    Promise.all([
      fetchProducts({ lang: apiLang, onlyActive: true }),
      fetchCategories({ lang: apiLang }),
    ])
      .then(([prods, cats]) => {
        if (cancelled) return;
        setAllProducts(Array.isArray(prods) ? prods : []);
        setApiCategories(Array.isArray(cats) ? cats : []);
      })
      .catch((err) => console.error("ProductsPage fetch error:", err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Build tabs from API categories
  const categoryTabs = useMemo(() => {
    const tabs = [{ id: "all", label: t("productsPage.tabs.all") }];
    apiCategories.forEach((cat) => {
      tabs.push({ id: String(cat.id), label: cat.name });
    });
    return tabs;
  }, [apiCategories, t]);

  const filteredProducts = useMemo(() => {
    if (activeCategory === "all") return allProducts;
    return allProducts.filter((p) => String(p.categoryId) === activeCategory);
  }, [activeCategory, allProducts]);

  // Convert product images to the format used by ProductPreviewModal (images: [{url}])
  const toPreviewProduct = (p) => {
    const images = [];

    // إذا عندك Array من الصور أصلاً من الباك اند:
    if (Array.isArray(p.images) && p.images.length) {
      // تأكد وجود url
      p.images.forEach((img, i) => {
        const url = img?.url || img?.imageUrl || img?.path;
        if (url) images.push({ id: img.id ?? i, url });
      });
    } else {
      // إذا عندك صورتين منفصلات:
      if (p.primaryImageUrl) images.push({ id: "primary", url: p.primaryImageUrl });
      if (p.secondaryImageUrl) images.push({ id: "secondary", url: p.secondaryImageUrl });
    }

    return {
      ...p,
      images, // المودال يستخدمها
    };
  };

  const openPreview = (p) => {
    setSelectedProduct(toPreviewProduct(p));
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setSelectedProduct(null);
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Hero */}
      <div className="relative h-[50vh] flex items-center justify-center overflow-hidden bg-[#1a0505]">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1920&q=80"
            alt="Products Hero"
            className="w-full h-full object-cover opacity-50 animate-slow-scale"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#c32228]/30 via-[#c32228]/10 to-transparent" />
        </div>

        <div className="relative z-10 text-center px-4 pt-10">
          <span className="text-[#c32228] font-bold tracking-[0.4em] text-sm uppercase mb-4 block animate-fade-in-down">
            {t("productsPage.tagline")}
          </span>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 drop-shadow-xl animate-scale-in">
            {t("productsPage.title")}{" "}
            <span className="text-[#d6ad60]">{t("productsPage.titleHighlight")}</span>
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-[80px] z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm py-4">
        <div className="container mx-auto px-6 overflow-x-auto">
          <div className="flex flex-nowrap md:flex-wrap items-center justify-start md:justify-center gap-8 min-w-max">
            {categoryTabs.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`text-base font-bold pb-2 border-b-2 transition-all duration-300 ${
                  activeCategory === cat.id
                    ? "text-[#c32228] border-[#c32228]"
                    : "text-gray-400 border-transparent hover:text-black"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="container mx-auto px-6 py-16">
        {loading && (
          <div className="text-center text-gray-400 font-bold">Loading...</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {filteredProducts.map((product, index) => (
            <button
              key={product.id}
              type="button"
              onClick={() => openPreview(product)}
              className="group flex flex-col bg-white rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-[#c32228] text-left"
              style={{
                animation: `fadeInUp 0.6s ease-out forwards ${index * 0.05}s`,
                opacity: 0,
              }}
            >
              <div className="relative h-64 overflow-hidden bg-gray-100">
                <img
                  src={
                    product.primaryImageUrl ||
                    (Array.isArray(product.images) && product.images[0]?.url) ||
                    "https://images.unsplash.com/photo-1599487488170-d11ec9c172f0?w=600"
                  }
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              <div className="p-5 flex flex-col flex-1">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-xl font-bold text-[#121212] group-hover:text-[#c32228] transition-colors">
                    {product.name}
                  </h3>

                  <div className="flex items-baseline gap-1 text-[#c32228] font-serif font-bold text-lg">
                    <span>{product.price}</span>
                    <span className="text-xs text-gray-500">{product.currency}</span>
                  </div>
                </div>

                <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">
                  {product.description}
                </p>

                <div className="pt-4 border-t border-dashed border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    {product.categoryName || t("productsPage.specialDish")}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* نفس المودال تبعك 100% */}
      <ProductPreviewModal
        isOpen={isPreviewOpen}
        onClose={closePreview}
        product={selectedProduct}
      />
    </div>
  );
}
